import React, { useState, useEffect } from 'react';
import {
  CheckCheck,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Calendar,
  User,
  Search,
  Filter,
  Camera,
  Award,
  ShieldCheck,
  XCircle,
  FileCheck,
  ArrowRight,
  ClipboardList,
  Plus,
  Trash2,
  AlertCircle,
  Clock,
  ShieldAlert,
} from 'lucide-react';
import { TicketItem, WorkOrderItem, VerificationChecklistItem } from '../types/index.ts';
import { useAuth } from '../context/AuthContext.tsx';

interface JobVerificationPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const JobVerificationPage: React.FC<JobVerificationPageProps> = ({ onNavigate }) => {
  const { currentUser, currentRole } = useAuth();
  const role = (currentRole || 'ADMIN').toUpperCase();
  const isSupervisorOrAdmin =
    role === 'ADMIN' ||
    role === 'MANAGEMENT' ||
    role === 'SUPERVISOR' ||
    role === 'PROJECT_MANAGER';

  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<'PENDING' | 'VERIFIED' | 'CORRECTIONS' | 'ALL'>('PENDING');

  // Verification Modal State
  const [verifyingTicket, setVerifyingTicket] = useState<TicketItem | null>(null);
  const [associatedWorkOrder, setAssociatedWorkOrder] = useState<WorkOrderItem | null>(null);

  // 7-Point Supervisor Verification Checklist State
  const [sevenPointChecklist, setSevenPointChecklist] = useState({
    scopeVerified: true,
    qualityVerified: true,
    materialsVerified: true,
    photosVerified: true,
    branchAcknowledgmentVerified: true,
    measurementsVerified: true,
    cleanlinessVerified: true,
  });

  // Punch-list manager items
  const [punchListItems, setPunchListItems] = useState<string[]>([]);
  const [newPunchItem, setNewPunchItem] = useState('');
  const [punchListResolved, setPunchListResolved] = useState(true);

  const [inspectorName, setInspectorName] = useState(currentUser?.name || 'Engr. Bilal Hashmi');
  const [remarks, setRemarks] = useState(
    'Joint inspection completed with UBL Branch Operations In-charge. 7-point audit verified against approved BOQ.'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tktRes, woRes] = await Promise.all([
        fetch('/api/tickets').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/work-orders').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (Array.isArray(tktRes)) setTickets(tktRes);
      if (Array.isArray(woRes)) setWorkOrders(woRes);
    } catch (err: any) {
      console.warn('Job verification load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openVerificationModal = (ticket: TicketItem) => {
    setVerifyingTicket(ticket);
    const linkedWo = workOrders.find((w) => w.ticketId === ticket.id) || workOrders[0] || null;
    setAssociatedWorkOrder(linkedWo);
    setInspectorName(currentUser?.name || 'Engr. Bilal Hashmi');
    setSevenPointChecklist({
      scopeVerified: true,
      qualityVerified: true,
      materialsVerified: true,
      photosVerified: true,
      branchAcknowledgmentVerified: true,
      measurementsVerified: true,
      cleanlinessVerified: true,
    });
    setPunchListItems([]);
    setNewPunchItem('');
    setPunchListResolved(true);
    setRemarks(
      `Joint supervisor audit for ${ticket.branchName || 'Branch'}. 7-point quality criteria inspected.`
    );
  };

  const handleAddPunchItem = () => {
    if (!newPunchItem.trim()) return;
    setPunchListItems([...punchListItems, newPunchItem.trim()]);
    setNewPunchItem('');
    setPunchListResolved(false);
  };

  const handleRemovePunchItem = (index: number) => {
    const updated = punchListItems.filter((_, i) => i !== index);
    setPunchListItems(updated);
    if (updated.length === 0) {
      setPunchListResolved(true);
    }
  };

  const allSevenChecked =
    sevenPointChecklist.scopeVerified &&
    sevenPointChecklist.qualityVerified &&
    sevenPointChecklist.materialsVerified &&
    sevenPointChecklist.photosVerified &&
    sevenPointChecklist.branchAcknowledgmentVerified &&
    sevenPointChecklist.measurementsVerified &&
    sevenPointChecklist.cleanlinessVerified;

  const handleVerificationSubmit = async (outcome: 'VERIFIED' | 'REQUIRES_CORRECTION' | 'REJECTED') => {
    if (!verifyingTicket) return;
    const woId = associatedWorkOrder?.id || 1;

    try {
      setIsSubmitting(true);
      const isPassed = outcome === 'VERIFIED';
      const statusParam = outcome === 'VERIFIED' ? 'PASSED' : outcome === 'REQUIRES_CORRECTION' ? 'CORRECTIONS_NEEDED' : 'REJECTED';

      // 1. Submit to 7-point Verification API linked to work order
      const verifyRes = await fetch(`/api/work-orders/${woId}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': role,
        },
        body: JSON.stringify({
          ticketId: verifyingTicket.id,
          verifiedBy: inspectorName,
          scopeVerified: sevenPointChecklist.scopeVerified,
          qualityVerified: sevenPointChecklist.qualityVerified,
          materialsVerified: sevenPointChecklist.materialsVerified,
          photosVerified: sevenPointChecklist.photosVerified,
          measurementsVerified: sevenPointChecklist.measurementsVerified,
          branchAcknowledgmentVerified: sevenPointChecklist.branchAcknowledgmentVerified,
          cleanlinessVerified: sevenPointChecklist.cleanlinessVerified,
          punchListItems,
          punchListResolved: isPassed ? true : punchListResolved,
          status: statusParam,
          remarks: remarks || (isPassed ? 'All 7 verification checkpoints passed.' : 'Punch list rectification required.'),
        }),
      });

      // 2. Synchronize Ticket closure verification
      await fetch(`/api/tickets/${verifyingTicket.id}/verify-closure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': role,
        },
        body: JSON.stringify({
          verificationStatus: outcome,
          verifiedBy: inspectorName,
          remarks,
        }),
      });

      if (verifyRes.ok) {
        if (outcome === 'VERIFIED') {
          setToastMessage(`Job for Ticket ${verifyingTicket.ticketNumber} VERIFIED! Completion certificate generation now unlocked.`);
        } else if (outcome === 'REQUIRES_CORRECTION') {
          setToastMessage(`Ticket ${verifyingTicket.ticketNumber} marked REQUIRES_CORRECTION. ${punchListItems.length} punch list item(s) logged.`);
        } else {
          setToastMessage(`Ticket ${verifyingTicket.ticketNumber} REJECTED. Returned to field staff.`);
        }
        setVerifyingTicket(null);
        await fetchData();
      } else {
        const errData = await verifyRes.json();
        setToastMessage(errData.error || 'Verification submission failed');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Verification submission error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToastMessage(null), 6000);
    }
  };

  const pendingVerification = tickets.filter(
    (t) => (t.verificationStatus !== 'VERIFIED' && t.verificationStatus !== 'PASSED') || t.status === 'COMPLETED' || t.status === 'VERIFICATION_PENDING'
  );
  const verifiedJobs = tickets.filter((t) => t.verificationStatus === 'VERIFIED' || t.verificationStatus === 'PASSED');
  const correctionJobs = tickets.filter(
    (t) => t.verificationStatus === 'CORRECTIONS_NEEDED' || t.verificationStatus === 'REQUIRES_CORRECTION' || t.verificationStatus === 'REJECTED'
  );

  const filteredTickets = tickets.filter((t) => {
    const isV = t.verificationStatus === 'VERIFIED' || t.verificationStatus === 'PASSED';
    const isC = t.verificationStatus === 'CORRECTIONS_NEEDED' || t.verificationStatus === 'REQUIRES_CORRECTION' || t.verificationStatus === 'REJECTED';
    const matchesTab =
      filterTab === 'ALL' ||
      (filterTab === 'PENDING' && !isV) ||
      (filterTab === 'VERIFIED' && isV) ||
      (filterTab === 'CORRECTIONS' && isC);
    const matchesSearch =
      t.ticketNumber.toLowerCase().includes(search.toLowerCase()) ||
      (t.branchName || '').toLowerCase().includes(search.toLowerCase()) ||
      t.subject.toLowerCase().includes(search.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Job Verification &amp; Punch List Management
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-indigo-100 text-indigo-900 border border-indigo-200">
              7-Point Joint Sign-off
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Enforce supervisor verification checklist before completion certificate generation. Punch list items link directly to existing work order.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => onNavigate('completion')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
          >
            <Award className="w-3.5 h-3.5 text-blue-600" />
            Completion Certificates
          </button>
          <button
            onClick={() => onNavigate('site-work')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-[#0f2942] text-white hover:bg-[#183d5f] shadow-xs transition-colors"
          >
            <ClipboardList className="w-3.5 h-3.5 text-amber-400" />
            Site Execution Hub
          </button>
        </div>
      </div>

      {/* Permission banner if non-supervisor */}
      {!isSupervisorOrAdmin && (
        <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2.5 text-xs text-amber-950">
          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
          <span>
            <strong>Read-Only Access:</strong> Supervisor or Management credentials required to approve joint verification sign-offs.
          </span>
        </div>
      )}

      {toastMessage && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-medium text-emerald-900 flex items-center gap-2 shadow-xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Supervisor QA</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{pendingVerification.length}</div>
          <div className="text-[11px] text-amber-700 mt-0.5">Awaiting 7-point checklist verification</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Formally Verified</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{verifiedJobs.length}</div>
          <div className="text-[11px] text-emerald-700 mt-0.5">Certificate generation enabled</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-rose-600 uppercase tracking-wider">Requires Correction</div>
          <div className="text-2xl font-bold text-rose-600 mt-1">{correctionJobs.length}</div>
          <div className="text-[11px] text-rose-700 mt-0.5">Active punch-list items on work order</div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilterTab('PENDING')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterTab === 'PENDING'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              Pending QA ({pendingVerification.length})
            </button>
            <button
              onClick={() => setFilterTab('VERIFIED')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterTab === 'VERIFIED'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              Verified ({verifiedJobs.length})
            </button>
            <button
              onClick={() => setFilterTab('CORRECTIONS')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterTab === 'CORRECTIONS'
                  ? 'bg-rose-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              Corrections Needed ({correctionJobs.length})
            </button>
            <button
              onClick={() => setFilterTab('ALL')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                filterTab === 'ALL'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              All ({tickets.length})
            </button>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search ticket, branch, subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-full sm:w-64"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                <th className="py-3 px-4">Ticket #</th>
                <th className="py-3 px-4">Branch</th>
                <th className="py-3 px-4">Subject</th>
                <th className="py-3 px-4">Current Status</th>
                <th className="py-3 px-4">Verification State</th>
                <th className="py-3 px-4">Inspector</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400">
                    No tickets match the selected verification filter.
                  </td>
                </tr>
              ) : (
                filteredTickets.map((t) => {
                  const isV = t.verificationStatus === 'VERIFIED' || t.verificationStatus === 'PASSED';
                  const isC = t.verificationStatus === 'CORRECTIONS_NEEDED' || t.verificationStatus === 'REQUIRES_CORRECTION';
                  return (
                    <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-slate-900">{t.ticketNumber}</td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{t.branchName}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{t.branchCode}</div>
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        <div className="font-medium text-slate-800 line-clamp-1">{t.subject}</div>
                        <div className="text-[11px] text-slate-500">{t.category}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                          {t.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isV ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            VERIFIED
                          </span>
                        ) : isC ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-800 border border-rose-200">
                            <AlertCircle className="w-3 h-3 text-rose-600" />
                            CORRECTION REQ.
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                            <AlertTriangle className="w-3 h-3 text-amber-600" />
                            PENDING QA
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{t.verifiedBy || '—'}</td>
                      <td className="py-3 px-4 text-right">
                        {!isV ? (
                          <button
                            onClick={() => openVerificationModal(t)}
                            disabled={!isSupervisorOrAdmin}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 text-white transition-colors inline-flex items-center gap-1 shadow-2xs"
                          >
                            <CheckCheck className="w-3.5 h-3.5" />
                            7-Point Audit
                          </button>
                        ) : (
                          <button
                            onClick={() => onNavigate('completion')}
                            className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
                          >
                            Generate Certificate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 7-POINT SUPERVISOR VERIFICATION MODAL & PUNCH-LIST MANAGER */}
      {verifyingTicket && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl max-h-[92vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-700" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    7-Point Supervisor Verification: {verifyingTicket.ticketNumber}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    {associatedWorkOrder ? `Work Order: ${associatedWorkOrder.workOrderNumber}` : 'Joint Sign-off Audit'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setVerifyingTicket(null)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl text-blue-900 space-y-1">
                <div className="font-bold text-xs">{verifyingTicket.branchName} ({verifyingTicket.branchCode})</div>
                <div className="text-[11px] text-blue-700">{verifyingTicket.subject}</div>
              </div>

              {/* 7-POINT CHECKLIST */}
              <div className="space-y-2">
                <div className="font-bold text-slate-800 uppercase text-[11px] tracking-wider flex items-center justify-between">
                  <span>Seven-Point Quality Checklist</span>
                  <span className="text-[11px] font-normal text-slate-500">
                    {Object.values(sevenPointChecklist).filter(Boolean).length}/7 Verified
                  </span>
                </div>

                <div className="space-y-1.5 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                  {/* Point 1 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.scopeVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, scopeVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">1. Approved scope / BOQ matched</strong>
                      <span className="text-[11px] text-slate-500">Physical installation matches client-approved bill of quantities items.</span>
                    </div>
                  </label>

                  {/* Point 2 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.qualityVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, qualityVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">2. Work quality acceptable</strong>
                      <span className="text-[11px] text-slate-500">Workmanship, alignment, and aesthetic finishing meet banking premises standards.</span>
                    </div>
                  </label>

                  {/* Point 3 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.materialsVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, materialsVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">3. Materials and required work completed</strong>
                      <span className="text-[11px] text-slate-500">Specified brand materials used and all tasks executed to completion.</span>
                    </div>
                  </label>

                  {/* Point 4 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.photosVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, photosVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">4. Before / after evidence available</strong>
                      <span className="text-[11px] text-slate-500">Geotagged photographic proof captured at both start and finish.</span>
                    </div>
                  </label>

                  {/* Point 5 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.branchAcknowledgmentVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, branchAcknowledgmentVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">5. Branch representative acknowledgment</strong>
                      <span className="text-[11px] text-slate-500">UBL Branch Manager / Operations In-charge signed off completion.</span>
                    </div>
                  </label>

                  {/* Point 6 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.measurementsVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, measurementsVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">6. Measurements verified where applicable</strong>
                      <span className="text-[11px] text-slate-500">Dimensions, square footage, and linear lengths measured and confirmed on site.</span>
                    </div>
                  </label>

                  {/* Point 7 */}
                  <label className="flex items-start gap-2.5 p-1.5 rounded-lg hover:bg-white cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sevenPointChecklist.cleanlinessVerified}
                      onChange={(e) => setSevenPointChecklist({ ...sevenPointChecklist, cleanlinessVerified: e.target.checked })}
                      className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <strong className="text-slate-900 block">7. Site cleanliness and finishing acceptable</strong>
                      <span className="text-[11px] text-slate-500">Debris cleared, surfaces cleaned, and banking hall ready for operations.</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* PUNCH-LIST MANAGER */}
              <div className="space-y-2 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-800 uppercase text-[11px] tracking-wider flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    Punch-List Manager (Correction Items)
                  </span>
                  <span className="text-[10px] text-slate-500">
                    Links to existing work order ({associatedWorkOrder?.workOrderNumber || 'WO'})
                  </span>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter punch-list correction defect (e.g. touchup paint on teller corner)..."
                    value={newPunchItem}
                    onChange={(e) => setNewPunchItem(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPunchItem();
                      }
                    }}
                    className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAddPunchItem}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Item
                  </button>
                </div>

                {punchListItems.length > 0 && (
                  <div className="space-y-1.5 pt-2">
                    {punchListItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-white p-2 rounded-lg border border-slate-200 text-xs"
                      >
                        <span className="text-slate-800">
                          <strong>#{idx + 1}.</strong> {item}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemovePunchItem(idx)}
                          className="text-slate-400 hover:text-rose-600 p-0.5"
                          title="Remove punch item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={punchListResolved}
                        onChange={(e) => setPunchListResolved(e.target.checked)}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-xs font-semibold text-slate-700">
                        All punch-list correction items resolved &amp; rectified on site
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Inspector info & remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Supervisor / Lead Inspector</label>
                  <input
                    type="text"
                    value={inspectorName}
                    onChange={(e) => setInspectorName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Audit Outcome Status</label>
                  <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold font-mono">
                    {allSevenChecked && (punchListItems.length === 0 || punchListResolved)
                      ? 'PASSED (VERIFIED)'
                      : punchListItems.length > 0
                      ? 'REQUIRES_CORRECTION'
                      : 'DEFECTS DETECTED'}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Supervisor Joint Audit Remarks</label>
                <textarea
                  rows={2}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Action buttons */}
              <div className="pt-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleVerificationSubmit('REQUIRES_CORRECTION')}
                    disabled={isSubmitting || punchListItems.length === 0}
                    className="px-3 py-2 text-xs font-semibold rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 transition-colors disabled:opacity-40"
                    title="Log correction items without creating duplicate ticket"
                  >
                    Flag Correction Punch-List
                  </button>

                  <button
                    type="button"
                    onClick={() => handleVerificationSubmit('REJECTED')}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-xs font-semibold rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-300 transition-colors disabled:opacity-40"
                  >
                    Reject Audit
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVerifyingTicket(null)}
                    className="px-3.5 py-2 text-xs font-semibold rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => handleVerificationSubmit('VERIFIED')}
                    disabled={
                      isSubmitting ||
                      !allSevenChecked ||
                      (punchListItems.length > 0 && !punchListResolved)
                    }
                    className="px-4 py-2 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5 shadow-xs"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {isSubmitting ? 'Verifying...' : 'Approve & Pass Verification'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
